<template>
  <div class="video-manage-container">
    <CourseCategoryManage
      :categories="categoryRecords"
      :loading="categoryLoading"
      @select="handleCategorySelect"
      @changed="fetchCategory"
    />
    <div class="course-content">
      <!-- 筛选栏 -->
      <div class="filter-bar">
      <div class="filter-item">
        <label>课程名称</label>
        <el-input
          v-model="searchForm.courseName"
          placeholder="请输入课程名称"
          size="small"
          clearable
          style="width: 200px;"
          @keyup.enter.native="handleSearch"
          @clear="handleSearch"
        ></el-input>
      </div>
      <div class="filter-item">
        <label>课程状态</label>
        <el-select v-model="searchForm.status" placeholder="请选择" size="small" clearable style="width: 150px;">
          <el-option label="正常" value="1"></el-option>
          <el-option label="停用" value="0"></el-option>
        </el-select>
      </div>
      <el-button type="primary" icon="el-icon-search" size="small" @click="handleSearch">搜索</el-button>
      <el-button icon="el-icon-refresh-left" size="small" @click="handleReset">重置</el-button>
      <div class="filter-right">
        <el-button type="primary" plain size="small" icon="el-icon-plus" @click="handleAdd">新增</el-button>
      </div>
    </div>

    <!-- 视频卡片列表 -->
    <div class="video-grid" v-loading="loading">
      <div
        v-for="item in tableData"
        :key="item.id"
        class="video-card"
      >
        <div
          class="video-thumb"
          :class="{ 'has-cover': item.coverUrl }"
          :style="item.coverUrl ? { backgroundImage: `url(${resolveCoverUrl(item.coverUrl)})` } : {}"
          @click="handleChapter(item)"
        >
          <img v-if="item.coverUrl" :src="resolveCoverUrl(item.coverUrl)" class="thumb-image" alt="封面" />
          <div v-else class="play-circle">
            <div class="play-circle-ring"></div>
            <i class="el-icon-video-play play-icon"></i>
          </div>
          <el-tag :type="item.status === 1 ? 'success' : 'danger'" size="mini" class="status-tag">
            {{ item.status === 1 ? '正常' : '禁用' }}
          </el-tag>
        </div>
        <div class="video-info">
          <div class="video-title" :title="item.courseName">{{ item.courseName }}</div>
          <div class="info-row">
            <el-tag size="mini" type="info" class="course-type-tag">{{ topLevelNameMap[String(item.categoryId)] || item.categoryName }}</el-tag>
          </div>
          <div class="info-row meta-row">
            <div class="meta-item">
              <i class="el-icon-user"></i>
              <span>讲师：{{ item.instructor }}</span>
            </div>
          </div>
          <div class="info-row meta-row">
            <div class="meta-item">
              <i class="el-icon-time"></i>
              <span>{{ item.createTime }}</span>
            </div>
          </div>
          <div class="action-row">
            <el-button type="text" size="mini" icon="el-icon-edit" @click="handleEdit(item)">编辑</el-button>
            <el-divider direction="vertical"></el-divider>
            <el-button type="text" size="mini" icon="el-icon-delete" style="color: #f56c6c;" @click="handleDelete(item)">删除</el-button>
          </div>
        </div>
      </div>
      <div v-if="!loading && tableData.length === 0" class="empty-tip">
        <i class="el-icon-folder-opened"></i>
        <p>暂无课程数据</p>
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination-wrap">
      <span class="pagination-info">共 {{ total }} 条</span>
      <el-pagination
        background
        layout="sizes, prev, pager, next"
        :page-sizes="[10, 20, 50]"
        :current-page.sync="page"
        :page-size.sync="limit"
        :total="total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      ></el-pagination>
      <span class="goto-page">
        前往 <el-input v-model="jumpPage" size="mini" style="width: 50px;" @keyup.enter.native="handleJumpPage"></el-input> 页
      </span>
    </div>

    <VideoAdd :visible.sync="addDialogVisible" :edit-data="editRow" @saved="handleCourseSaved" />
    </div>
  </div>
</template>

<script>
import VideoAdd from './VideoAdd.vue'
import CourseCategoryManage from './components/CourseCategoryManage.vue'
import { getCoursePage, deleteCourse, getCourseCategoryPage } from '@/api/video'

export default {
  name: 'VideoManage',
  components: { VideoAdd, CourseCategoryManage },
  data() {
    return {
      loading: false,
      addDialogVisible: false,
      editRow: null,
      searchForm: {
        courseName: '',
        status: '',
        courseType: null,
      },
      // 课程分类树数据
      categoryLoading: false,
      categoryRecords: [],
      tableData: [],
      categoryMap: {},
      // 分类衍生映射：一级分类名、子孙 id 列表
      topLevelNameMap: {},
      descendantIdsMap: {},
      total: 0,
      page: 1,
      limit: 10,
      jumpPage: '',
    }
  },
  created() {
    this.initData();
    this.fetchCategory();
  },
  methods: {
    async fetchCategory() {
      this.categoryLoading = true
      try {
        const res = await getCourseCategoryPage({ page: 1, limit: 1000 })
        if (res.code === 200 && res.data) {
          const records = (res.data.records || [])
          console.log('[课程分类返回]', { raw: res.data, count: records.length })
          this.categoryRecords = records
          this.categoryMap = records.reduce((map, item) => {
            map[item.id] = item.categoryName
            return map
          }, {})
          // 构建分类树，并计算一级分类名 + 所有子孙 id
          this.rebuildCategoryMaps(records)
          this.tableData = this.tableData.map(item => ({
            ...item,
            categoryName: item.categoryName || this.categoryMap[item.categoryId] || '',
          }))
        }
      } catch (e) {
        console.error('获取课程分类失败', e)
      } finally {
        this.categoryLoading = false
      }
    },
    rebuildCategoryMaps(records) {
      const map = {}
      const tree = []
      records.forEach(r => {
        map[String(r.id)] = { ...r, id: String(r.id), children: [] }
      })
      records.forEach(r => {
        const node = map[String(r.id)]
        const pid = r.parentId == null ? '' : String(r.parentId)
        if (!pid || !map[pid]) {
          tree.push(node)
        } else {
          map[pid].children.push(node)
        }
      })
      // 一级分类名映射 + 子孙 id 映射
      const topMap = {}
      const descMap = {}
      const walk = (node) => {
        const collect = (n) => {
          const ids = [n.id]
          if (n.children && n.children.length) {
            n.children.forEach(c => ids.push(...collect(c)))
          }
          return ids
        }
        const ids = collect(node)
        descMap[node.id] = ids
        // 一级分类就是自身；子孙节点向上递归找一级
        topMap[node.id] = node.categoryName
        if (node.children && node.children.length) {
          node.children.forEach(c => {
            const setTop = (sub, root) => {
              topMap[sub.id] = root.categoryName
              if (sub.children && sub.children.length) {
                sub.children.forEach(s => setTop(s, root))
              }
            }
            setTop(c, node)
            walk(c)
          })
        }
      }
      tree.forEach(walk)
      this.topLevelNameMap = topMap
      this.descendantIdsMap = descMap
      console.log('[分类映射]', { topMap, descMap })
    },
    resolveCoverUrl(url) {
      if (!url) return ''
      if (/^https?:\/\//i.test(url)) return url
      let path = url.startsWith('/') ? url : `/${url}`
      path = path.replace(/^\/home\/file_upload/, '/files')
      const base = (
        this.$store.state.IPAss.yl ||
        process.env.VUE_APP_PREVIEW_FILE_URL ||
        ''
      ).replace(/\/$/, '')
      return `${base}${path}`
    },
    async initData() {
      this.loading = true;
      try {
        const condition = { courseName: this.searchForm.courseName }
        if (this.searchForm.status !== '') condition.status = Number(this.searchForm.status)
        if (this.searchForm.courseType) {
          const ids = this.descendantIdsMap[String(this.searchForm.courseType)] || [this.searchForm.courseType]
          if (ids.length > 1) {
            condition.categoryIds = ids
          } else {
            condition.categoryId = ids[0]
          }
        }
        const res = await getCoursePage({ page: this.page, limit: this.limit, condition })
        if (res.code === 200 && res.data) {
          this.tableData = (res.data.records || []).map(item => ({
            ...item,
            categoryName: item.categoryName || this.categoryMap[item.categoryId] || '',
          }))
          this.total = res.data.total || 0
        }
      } catch (e) {
        console.error('获取课程列表失败', e)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1;
      this.initData();
    },
    handleCategorySelect(categoryId) {
      this.searchForm.courseType = categoryId
      this.handleSearch()
    },
    handleCourseSaved() {
      this.initData()
      this.fetchCategory()
    },
    handleReset() {
      this.searchForm = {
        courseName: '',
        status: '',
        courseType: null,
      };
      this.page = 1;
      this.initData();
    },
    handleSizeChange(val) {
      this.page = 1;
      this.limit = val;
      this.initData();
    },
    handleCurrentChange(val) {
      this.page = val;
      this.initData();
    },
    handleJumpPage() {
      const p = parseInt(this.jumpPage);
      if (p && p > 0 && p <= Math.ceil(this.total / this.limit)) {
        this.page = p;
        this.initData();
      }
      this.jumpPage = '';
    },
    handleAdd() {
      this.editRow = null
      this.addDialogVisible = true
    },
    handleEdit(row) {
      this.editRow = row
      this.addDialogVisible = true
    },
    handleChapter(row) {
      this.$router.push({
        path: '/video/VideoChapter',
        query: {
          courseId: row.id,
          courseName: row.courseName,
          courseType: row.categoryName,
          instructor: row.instructor,
          createTime: row.createTime,
          coverUrl: this.resolveCoverUrl(row.coverUrl),
          description: row.description || '',
        },
      })
    },
    async handleDelete(row) {
      try {
        await this.$confirm('确定要删除该课程吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        })
        const res = await deleteCourse(row.id)
        if (res.code === 200 && res.data) {
          this.$message.success('删除成功')
          this.initData()
        }
      } catch (e) {
        // 取消操作
      }
    },
  }
}
</script>

<style scoped lang="scss">
.video-manage-container {
  padding: 16px 20px;
  display: flex;
  gap: 16px;
  height: calc(100vh - 120px);
  min-height: 0;
  box-sizing: border-box;
  align-items: stretch;
  overflow: hidden;
}

.course-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

/* 筛选栏 */
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  background: #fff;
  padding: 14px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
  flex-shrink: 0;

  .filter-item {
    display: flex;
    align-items: center;
    font-size: 13px;

    label {
      white-space: nowrap;
      margin-right: 6px;
      color: #606266;
    }

    ::v-deep .vue-treeselect {
      line-height: 1;
    }

    ::v-deep .vue-treeselect__control {
      height: 32px;
      border-radius: 4px;
      border-color: #dcdfe6;
    }

    ::v-deep .vue-treeselect__placeholder {
      line-height: 30px;
      color: #c0c4cc;
    }
  }

  .filter-right {
    margin-left: auto;
  }
}

/* 视频卡片网格 */
.video-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  flex: 1;
  overflow-y: auto;
  align-content: start;
  min-height: 0;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (max-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.video-card {
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid #ebeef5;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);

    .play-circle {
      transform: translate(-50%, -50%) scale(1.12);
      animation-play-state: paused;
    }
  }

  .video-thumb {
    position: relative;
    width: 100%;
    padding-top: 56%;
    background: linear-gradient(135deg, #b3d8ff 0%, #7fb8ff 50%, #5ea0f0 100%);
    background-size: cover;
    background-position: center;
    cursor: pointer;
    overflow: hidden;

    &.has-cover::before {
      content: none;
    }

    .thumb-image {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background:
        radial-gradient(circle at 20% 25%, rgba(255, 255, 255, 0.35), transparent 45%),
        radial-gradient(circle at 80% 75%, rgba(255, 255, 255, 0.2), transparent 50%);
      pointer-events: none;
    }

    .play-circle {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 70px;
      height: 70px;
      border-radius: 50%;
      background: linear-gradient(135deg, rgba(64, 158, 255, 0.92) 0%, rgba(46, 121, 232, 0.92) 100%);
      backdrop-filter: blur(4px);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.25s ease, box-shadow 0.25s ease;
      box-shadow:
        0 6px 20px rgba(46, 121, 232, 0.45),
        inset 0 0 0 1.5px rgba(255, 255, 255, 0.5);
      animation: playPulse 2.4s ease-in-out infinite;

      .play-circle-ring {
        position: absolute;
        inset: -8px;
        border-radius: 50%;
        border: 1.5px solid rgba(255, 255, 255, 0.35);
        pointer-events: none;
      }

      .play-icon {
        font-size: 30px;
        color: #fff;
        margin-left: 4px;
        text-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
      }
    }

    @keyframes playPulse {
      0%, 100% {
        box-shadow:
          0 6px 20px rgba(46, 121, 232, 0.45),
          inset 0 0 0 1.5px rgba(255, 255, 255, 0.5);
      }
      50% {
        box-shadow:
          0 6px 28px rgba(46, 121, 232, 0.65),
          inset 0 0 0 1.5px rgba(255, 255, 255, 0.7);
      }
    }

    .status-tag {
      position: absolute;
      top: 8px;
      right: 8px;
      font-size: 11px;
      background: rgba(255, 255, 255, 0.95) !important;
      color: #67c23a !important;
      border: none !important;
      padding: 1px 8px !important;
      height: 20px !important;
      line-height: 18px !important;
      font-weight: 500;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    }
  }

  .video-info {
    padding: 10px 12px;

    .video-title {
      font-size: 13px;
      color: #303133;
      line-height: 1.4;
      height: 36px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 8px;
      font-weight: 600;
    }

    .info-row {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-bottom: 6px;

      &.meta-row {
        gap: 0;
        flex-wrap: wrap;
      }

      .meta-item {
        display: flex;
        align-items: center;
        font-size: 11px;
        color: #909399;
        margin-right: 8px;

        i {
          margin-right: 3px;
          font-size: 12px;
          color: #c0c4cc;
        }
      }

      .course-type-tag {
        background: linear-gradient(135deg, #e6f0ff 0%, #d9e8ff 100%) !important;
        color: #409eff !important;
        border: none !important;
        font-size: 11px !important;
        padding: 1px 8px !important;
        height: 20px !important;
        line-height: 18px !important;
        font-weight: 500;
        border-radius: 3px !important;
      }
    }

    .action-row {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px dashed #ebeef5;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 4px;

      .el-button--mini {
        font-size: 12px;
        padding: 2px 4px;
      }

      .el-divider--vertical {
        margin: 0;
      }
    }
  }
}

.empty-tip {
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 0;
  color: #c0c4cc;

  i {
    font-size: 48px;
    margin-bottom: 10px;
  }

  p {
    font-size: 14px;
  }
}

/* 分页 */
.pagination-wrap {
  margin-top: 16px;
  padding: 12px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #606266;
  flex-shrink: 0;

  .pagination-info {
    white-space: nowrap;
    margin-right: 20px;
  }

  ::v-deep .el-pagination {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 0;
  }

  ::v-deep .el-pagination .el-pagination__sizes,
  ::v-deep .el-pagination .el-pager,
  ::v-deep .el-pagination .btn-prev,
  ::v-deep .el-pagination .btn-next {
    margin: 0;
  }

  ::v-deep .el-pagination .el-pagination__jump {
    margin-left: 0;
  }

  .goto-page {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    margin-left: 20px;

    .el-input {
      margin: 0 4px;
    }
  }
}
</style>
